import { useCallback, useMemo } from 'react'
import { numberToHex } from 'viem'
import {
  usePublicClient,
  useSendTransaction as useWagmiSendTransaction,
  useWriteContract as useWagmiWriteContract,
} from 'wagmi'

type TransactionProvider = {
  request: (request: { method: string; params?: readonly unknown[] }) => Promise<unknown>
}

export const useTransactionFees = () => {
  const publicClient = usePublicClient()

  return useCallback(async () => {
    if (!publicClient) {
      throw new Error('Public client is not available')
    }

    const { maxFeePerGas, maxPriorityFeePerGas } = await publicClient.estimateFeesPerGas({
      type: 'eip1559',
    })

    return {
      maxFeePerGas,
      maxPriorityFeePerGas,
    }
  }, [publicClient])
}

// Applies EIP-1559 fees to transactions sent internally by Orbs, including cancellations.
export const useFeeAwareTransactionProvider = (
  provider: TransactionProvider | undefined
): TransactionProvider | undefined => {
  const getTransactionFees = useTransactionFees()

  return useMemo(() => {
    if (!provider) return undefined

    return {
      ...provider,
      request: async (request) => {
        if (request.method !== 'eth_sendTransaction' && request.method !== 'wallet_sendTransaction') {
          return provider.request(request)
        }

        const { maxFeePerGas, maxPriorityFeePerGas } = await getTransactionFees()
        const transactionRequest = { ...(request.params?.[0] as Record<string, unknown>) }
        delete transactionRequest.gasPrice

        return provider.request({
          ...request,
          params: [
            {
              ...transactionRequest,
              type: '0x2', // EIP-1559 transaction type
              maxFeePerGas: numberToHex(maxFeePerGas),
              maxPriorityFeePerGas: numberToHex(maxPriorityFeePerGas),
            },
            ...(request.params?.slice(1) ?? []),
          ],
        })
      },
    }
  }, [getTransactionFees, provider])
}

export const useSendTransaction = () => {
  const result = useWagmiSendTransaction()
  const { sendTransactionAsync } = result
  const getTransactionFees = useTransactionFees()

  const sendTransactionAsyncWithFees = useCallback(
    async (...args: Parameters<typeof sendTransactionAsync>) => {
      const [variables, options] = args
      const fees = await getTransactionFees()

      return sendTransactionAsync({ ...variables, ...fees } as typeof variables, options)
    },
    [getTransactionFees, sendTransactionAsync]
  ) as typeof sendTransactionAsync

  return {
    ...result,
    // Fee estimation is asynchronous, so callers must use the async mutation.
    sendTransaction: undefined,
    sendTransactionAsync: sendTransactionAsyncWithFees,
  }
}

export const useWriteContract = () => {
  const result = useWagmiWriteContract()
  const { writeContractAsync } = result
  const getTransactionFees = useTransactionFees()

  const writeContractAsyncWithFees = useCallback(
    async (...args: Parameters<typeof writeContractAsync>) => {
      const [variables, options] = args
      const fees = await getTransactionFees()

      return writeContractAsync({ ...variables, ...fees } as typeof variables, options)
    },
    [getTransactionFees, writeContractAsync]
  ) as typeof writeContractAsync

  return {
    ...result,
    // Fee estimation is asynchronous, so callers must use the async mutation.
    writeContract: undefined,
    writeContractAsync: writeContractAsyncWithFees,
  }
}
